/**
 * Session service for handling exam session lifecycle
 */

import { convertDifficultyStringsToIds } from '../../utils/difficultyMapping';

/**
 * Starts a new practice session
 * @param {Object} params - Session parameters
 * @param {Object} params.sessionSetup - The setup configuration for the session
 * @param {Object[]} params.systemCategories - The available system categories
 * @param {Object[]} params.disciplineCategories - The available discipline categories
 * @param {Function} params.setLoading - Function to set loading state
 * @param {Function} params.setError - Function to set error state
 * @param {Function} params.setCurrentSessionId - Function to set the current session ID
 * @param {Function} params.setSetupComplete - Function to set setup complete state
 * @param {Function} params.setTimeTracker - Function to set the time tracker
 * @param {Object} services - The services object
 * @returns {Promise<Object>} The session response
 */
export const startSession = async (params, services) => {
  const {
    sessionSetup,
    systemCategories,
    disciplineCategories, 
    setLoading,
    setError,
    setCurrentSessionId,
    setSetupComplete,
    setTimeTracker
  } = params;

  // Extract session parameters
  const systemIds = sessionSetup.system.includes('ALL') 
    ? [] 
    : sessionSetup.system.map(systemName => {
        const systemCategory = systemCategories.find(c => c.name === systemName);
        return systemCategory?.id;
      }).filter(id => id);

  const disciplineIds = sessionSetup.discipline.includes('ALL') 
    ? [] 
    : sessionSetup.discipline.map(disciplineName => {
        const disciplineCategory = disciplineCategories.find(c => c.name === disciplineName);
        return disciplineCategory?.id;
      }).filter(id => id);

  const categoryIds = [...systemIds, ...disciplineIds];  const difficultyValues = sessionSetup.difficulty.includes('ALL') 
    ? [] 
    : convertDifficultyStringsToIds(sessionSetup.difficulty);

  const userFolderIds = (sessionSetup.userFolders && sessionSetup.userFolders.length > 0) 
          ? sessionSetup.userFolders.filter(t => t !== "None" && t !== "" && t !== "null" && t !== null)
          : [];

  // Create the request according to the backend StartExamRequest schema
  const request = {
    // Required fields
    examTypeId: sessionSetup.examTypeId,
    userFolderIds: userFolderIds,
    type: "Practice", 
    Title: sessionSetup.title, // Note: Capital T as per backend API
    limit: parseInt(sessionSetup.questionCount) || 10 // Required: number of questions
  };

  // Add optional fields only when they are not set to "ALL" or default values
  if (difficultyValues.length > 0) {
    request.difficulty = difficultyValues;
  }

  if (categoryIds.length > 0) {
    request.categoryIds = categoryIds;
  }

  // Add question filter booleans only when a specific filter is selected
  if (sessionSetup.filterOption === "Questions I have never seen") {
    request.neverSeen = true;
  } else if (sessionSetup.filterOption === "Questions I have answered incorrectly") {
    request.answeredIncorrectly = true;
  } else if (sessionSetup.filterOption === "Questions I have answered correctly") {
    request.answeredCorrectly = true;
  } else if (sessionSetup.filterOption === "Questions I have seen before") {
    request.seenBefore = true;
  }

  try {
    setLoading(true);
    setError(null);
    // console.log('Starting exam session with request:', request);

    // Call the API to start a new exam session
    const response = await services.exam.startExamSession(request);
    // console.log('Response from startExamSession:', response);

    if (response && response.id) {
      setCurrentSessionId(response.id);
      setSetupComplete(true);
      
      // Reset the question timer
      setTimeTracker({
        questionStartTime: Date.now(),
        totalTimeSpent: 0
      });
      
      return response;
    } else {
      throw new Error('Invalid response from API');
    }
  } catch (error) {
    console.error('Error starting session:', error);
    // console.log('SessionService - Error status:', error.status);
    // console.log('SessionService - Error data:', error.data);
    // console.log('SessionService - Error response:', error.response);
    // console.log('SessionService - Error message:', error.message);
    
    // Check if this is a 403 error with detail field (like quota exceeded)
    // The error might be wrapped in different ways depending on the API response
    let errorDetail = null;
    
    // Check direct error data
    if (error.data && error.data.detail) {
      errorDetail = error.data.detail;
      // console.log('SessionService - Found detail in error.data:', errorDetail);
    }
    // Check if error data has status 403 with detail
    else if (error.data && error.data.status === 403 && error.data.detail) {
      errorDetail = error.data.detail;
      // console.log('SessionService - Found detail in error.data with status 403:', errorDetail);
    }
    // Check response data
    else if (error.response && error.response.data && error.response.data.detail) {
      errorDetail = error.response.data.detail;
      // console.log('SessionService - Found detail in error.response.data:', errorDetail);
    }
    // Check if response data has status 403 with detail
    else if (error.response && error.response.data && error.response.data.status === 403 && error.response.data.detail) {
      errorDetail = error.response.data.detail;
      // console.log('SessionService - Found detail in error.response.data with status 403:', errorDetail);
    }
    
    if (errorDetail) {
      setError(errorDetail);
    } else {
      setError('Failed to start the session. Please try again.');
    }
    
    throw error;
  } finally {
    setLoading(false);
  }
};

/**
 * Ends an exam session
 * @param {Object} params - Session parameters
 * @param {string} params.sessionId - The current session ID
 * @param {Object} params.timeTracker - The time tracker
 * @param {string} params.sessionTitle - The session title
 * @param {Function} params.setIsSessionEnding - Function to set session ending state
 * @param {Function} params.resetSessionState - Function to reset the session state
 * @param {Object} services - The services object
 * @returns {Promise<void>}
 */
export const endSession = async (params, services) => {
  const {
    sessionId,
    timeTracker,
    sessionTitle,
    setIsSessionEnding,
    resetSessionState
  } = params;

  if (!sessionId) return;
  
  try {
    setIsSessionEnding(true);
    
    // Calculate total time spent on the session (in seconds)
    const totalTimeSpent = timeTracker.totalTimeSpent + 
      Math.floor((Date.now() - timeTracker.questionStartTime) / 1000);
      // Call the API to end the session
    await services.exam.endExamSession(sessionId, {
      timeSpent: totalTimeSpent
    });
    
    // Reset session state
    resetSessionState();
    
  } catch (err) {
    console.error('Error ending practice session:', err);
    throw new Error('Failed to end practice session. Please try again later.');
  } finally {
    setIsSessionEnding(false);
  }
};
